import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormError } from "@/pages/auth/AuthShell";
import { TimezoneSelect } from "./TimezoneSelect";
import { api, errorMessage } from "@/lib/api";
import { PROPERTY_TYPES, US_STATES } from "@/lib/format";

export const emptyProperty = () => ({ name: "", address: "", city: "", state: "", county: "", timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles", property_type: null });

export const PropertyFields = ({ value, onChange }) => {
  const set = (k) => (v) => onChange({ ...value, [k]: typeof v === "string" || v === null ? v : v.target.value });
  return (
    <div className="space-y-4" data-testid="property-fields">
      <div className="space-y-2">
        <Label htmlFor="p-name">Property name</Label>
        <Input id="p-name" data-testid="property-name-input" value={value.name} onChange={set("name")} required placeholder="e.g. Our home, or a client's last name" className="h-11" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="p-address">Street address (optional)</Label>
        <Input id="p-address" data-testid="property-address-input" value={value.address} onChange={set("address")} className="h-11" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="p-city">City</Label>
          <Input id="p-city" data-testid="property-city-input" value={value.city} onChange={set("city")} required className="h-11" />
        </div>
        <div className="space-y-2">
          <Label>State</Label>
          <Select value={value.state} onValueChange={set("state")}>
            <SelectTrigger data-testid="property-state-select" className="h-11"><SelectValue placeholder="Choose a state" /></SelectTrigger>
            <SelectContent className="max-h-72">{US_STATES.map((s) => <SelectItem key={s} value={s} data-testid={`property-state-option-${s}`}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="p-county">County (optional)</Label>
          <Input id="p-county" data-testid="property-county-input" value={value.county} onChange={set("county")} placeholder="e.g. Los Angeles" className="h-11" />
          <p className="text-xs text-slate-500">Helps us check which local forms may apply.</p>
        </div>
        <div className="space-y-2">
          <Label>Property type (optional)</Label>
          <Select value={value.property_type || "none"} onValueChange={(v) => set("property_type")(v === "none" ? null : v)}>
            <SelectTrigger data-testid="property-type-select" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Not set</SelectItem>{PROPERTY_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Property timezone</Label>
        <TimezoneSelect value={value.timezone} onChange={set("timezone")} testId="property-timezone-select" />
      </div>
    </div>
  );
};

export const PropertyDialog = ({ workspaceId, property, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(property || emptyProperty());
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.state) return setError("Please choose a state.");
    setBusy(true);
    try {
      const body = { ...form, address: form.address || "", county: form.county || "" };
      if (property) await api.patch(`/workspaces/${workspaceId}/properties/${property.id}`, body);
      else await api.post(`/workspaces/${workspaceId}/properties`, body);
      onOpenChange(false); onSaved();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="property-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{property ? "Edit property" : "Add a property"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <PropertyFields value={form} onChange={setForm} />
          <FormError message={error} testId="property-form-error" />
          <Button type="submit" disabled={busy} data-testid="property-save-button" className="h-11 w-full bg-navy hover:bg-navy-2">{busy ? "Saving..." : "Save property"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
